import { Button } from "components/Button";
import { InputFormField } from "components/InputFormField";
import { Loading } from "components/Loading";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import { TableModifiedCards } from "components/TableModified/TableModifiedCards";
import { TableModifiedFilters } from "components/TableModified/TableModifiedFilters";
import { TableModifiedPagination } from "components/TableModified/TableModifiedPagination";
import { TableModifiedTable } from "components/TableModified/TableModifiedTable";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ImportCsvModal, RegistrationImport } from "./ImportCsvModal";
import { Container, TableSearchRegistrations } from "./styles";
import { Registration, useRegistrationsTable } from "./useRegistrationsTable";

const tableHead = [
  "Nome",
  "Empresa",
  "Cargo",
  "É Palestrante",
  "Ordem",
  "Status",
];

export const RegistrationsTable: React.FC = () => {
  const { push } = useHistory();
  const { id: eventId } = useParams<{ id: string }>();
  const { setToast } = useToast();
  const { connectGraphQLApi } = useGraphQLAuth();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleCreateRegistration = () => {
    push(`/events/${eventId}/registration/create`);
  };

  const handleEditRegistration = (registration: Registration) => {
    push(`/events/${eventId}/registration/${registration.registrationId}/edit`);
  };

  const handleImportCsv = async (registrations: RegistrationImport[]) => {
    setIsImporting(true);
    try {
      const mutation = `
        mutation BackofficeImportRegistrations($eventId: ID!, $registrations: [BackofficeRegistrationImportInput!]!) {
          backofficeImportRegistrations(eventId: $eventId, registrations: $registrations) {
            successCount
            errorCount
            errors
          }
        }
      `;

      const response = await connectGraphQLApi({
        query: mutation,
        variables: {
          eventId,
          registrations: registrations.map((reg) => ({
            customerCpf: reg.customerCpf,
            profile: {
              name: reg.profile.name,
              company: reg.profile.company,
              position: reg.profile.position,
              bio: reg.profile.bio,
              isSpeaker: reg.profile.isSpeaker,
              isVisible: reg.profile.isVisible,
              displayPosition: reg.profile.displayPosition,
            },
            importData: reg.importData,
          })),
        },
        errorMessage: "Erro ao importar inscrições",
        useNewApi: true,
      });

      const result = response?.data?.backofficeImportRegistrations;
      if (result) {
        setToast({
          type: result.errorCount > 0 ? "warning" : "success",
          message: `Importação concluída: ${result.successCount} sucesso(s), ${result.errorCount} erro(s)`,
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
      setIsImportModalOpen(false);
      // Refresh the registrations list
      window.location.reload();
    } catch (error: any) {
      setToast({
        type: "error",
        message: error?.message || "Erro ao importar inscrições",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const {
    loading,
    error,
    properties,
    registrationRows,
    registrationsCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    registrationsPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
    registrations,
    handleExportCsv,
    exporting,
  } = useRegistrationsTable();

  return (
    <Container>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "end",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <TableSearchRegistrations
          className="table-search-registrations"
          onSubmit={handleSearchTextFormSubmit}
          ref={searchTextFormRef}
          style={{ flex: 1 }}
        >
          <InputFormField
            label="Buscar Inscrições"
            autoFocus={true}
            name="searchText"
            marginBotton={"0"}
            backGround={"white"}
            defaultValue={searchText}
            block={true}
          />
          <Button label="Buscar" typeBtn="blue" type="submit" block={false} />
        </TableSearchRegistrations>

        <Button
          label="Exportar CSV"
          typeBtn="outline"
          onClick={handleExportCsv}
          load={exporting}
          block={false}
        />
        <Button
          label="Importar CSV"
          typeBtn="outline"
          onClick={() => setIsImportModalOpen(true)}
          block={false}
        />
        <Button
          label="Criar Inscrição"
          typeBtn="blue"
          onClick={handleCreateRegistration}
          block={false}
        />
      </div>

      <TableModifiedFilters
        filters={tableFilters}
        searchTextFilter={searchText}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onSearchTextRemove={handleSearchTextRemove}
      />
      {loading ? (
        <Loading />
      ) : error ? (
        <PlaceHolderNotExists title="Erro ao se comunicar com o servidor" />
      ) : (
        <>
          <TableModifiedCards properties={properties} />
          <TableModifiedTable
            headerColumns={tableHead}
            rows={registrationRows}
            onRowClick={(_, rowIndex) => {
              if (registrations && registrations[rowIndex]) {
                handleEditRegistration(registrations[rowIndex]);
              }
            }}
          />
        </>
      )}
      {registrationsCount > 0 ? (
        <TableModifiedPagination
          totalItemsCount={registrationsCount}
          pageNumber={pageNumber}
          rowsPerPage={registrationsPerPage}
          onPageSelected={handlePageSelected}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      ) : null}

      <ImportCsvModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onConfirm={handleImportCsv}
        isLoading={isImporting}
      />
    </Container>
  );
};
